import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Stack, Tooltip, Typography, styled } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDateInputValid } from '../../../../../components/DatePicker/Calendar';
import { CalendarCurrency } from '../../../../../components/DatePicker/CalendarCurrency';
import { Breakpoint, ContainerName } from '../../../../../constants/styles';
import { ViewTypeByData } from '../../../../../data-models/view-config.data-model';
import { useSelectedCurrency } from '../../../../../hooks/useSelectedCurrency';
import {
  dateToPlainDateString,
  isValidDate,
  nextQuarterEnd,
  previousQuarterEnd,
} from '../../../../../util/date-utilts';
import { selectedDateByViewPFJ } from '../../../state/PageStateJ';
import { selectedViewIdPFJ, selectedViewPFJ } from '../../../state/ViewStateJ';
import { portfolioDateShortcuts } from './portfolioDateShortcuts';

/** With the popup shut there is no dismissal to commit on, so a typed date applies on its own. */
const TYPED_DATE_APPLY_MS = 1500;

const StyledLabel = styled(Typography)`
  padding-right: 0.5rem;
  font-size: 0.825rem;
  text-align: right;
  @container ${ContainerName.headerContainer} (max-width: ${Breakpoint.hideBreadcrumbs}) {
    display: none;
  }
`;

const viewsWithDatePicker = new Set<ViewTypeByData>([
  ViewTypeByData.fund,
  ViewTypeByData.company,
  ViewTypeByData.transaction,
]);

export function PortfolioDatePicker() {
  const viewId = useAtomValue(selectedViewIdPFJ);
  const selectedView = useAtomValue(selectedViewPFJ);
  const [viewDate, setViewDate] = useAtom(selectedDateByViewPFJ(viewId));
  const { selectedCurrency } = useSelectedCurrency();

  // undefined = nothing uncommitted; null = the user cleared the field.
  const [pending, setPending] = useState<Date | null | undefined>(undefined);
  // A ref, not state: the open flag is read in the same event that may have just set it.
  const popupOpenRef = useRef(false);

  const shown = pending === undefined ? viewDate : pending;
  const stepFrom = shown && isValidDate(shown) ? shown : viewDate;

  const shortcutItems = useMemo(() => portfolioDateShortcuts(stepFrom), [stepFrom]);

  const applyDate = useCallback(
    (next: Date | null) => {
      // Cleared first, so a date that cannot be applied leaves the field on the one that is.
      setPending(undefined);
      // Future as-of dates are valid (views are forward-dated). Never bound this or the picker by today.
      if (!next || !isDateInputValid(next)) {
        return;
      }
      // Everything downstream reloads per distinct day, so a move within one day must not write.
      setViewDate((prev) => (dateToPlainDateString(prev) === dateToPlainDateString(next) ? prev : next));
    },
    [setViewDate]
  );

  const commitTyped = useMemo(() => debounce(applyDate, TYPED_DATE_APPLY_MS), [applyDate]);
  useEffect(() => () => commitTyped.cancel(), [commitTyped]);

  const onChange = useCallback(
    (next: Date | null) => {
      setPending(next);
      if (popupOpenRef.current) {
        return;
      }
      // An impossible date drops the one an earlier keystroke queued, and stays shown so it can be fixed.
      if (next !== null && !isDateInputValid(next)) {
        commitTyped.cancel();
        return;
      }
      commitTyped(next);
    },
    [commitTyped]
  );

  const onAccept = useCallback(
    (next: Date | null) => {
      commitTyped.cancel();
      applyDate(next);
    },
    [applyDate, commitTyped]
  );

  // A chevron has no popup to be dismissed from, so it applies as soon as it is clicked.
  const stepTo = useCallback(
    (next: Date) => {
      commitTyped.cancel();
      applyDate(next);
    },
    [applyDate, commitTyped]
  );

  const onOpen = useCallback(() => {
    commitTyped.cancel();
    popupOpenRef.current = true;
  }, [commitTyped]);

  const onClose = useCallback(() => {
    popupOpenRef.current = false;
    // Closing by the calendar icon reports no value of its own, so every exit applies what is
    // shown. Applying twice is free: the second pass is the same day and writes nothing.
    applyDate(shown);
  }, [applyDate, shown]);

  if (!selectedView || !viewsWithDatePicker.has(selectedView.viewType)) {
    return null;
  }

  return (
    <Stack display='grid' gridTemplateColumns={'auto 1fr'} alignItems={'center'} width={'100%'}>
      <StyledLabel role='label'>As Of Date</StyledLabel>
      <Stack direction='row' alignItems='center' gap='0.25rem'>
        <Tooltip title='Previous quarter'>
          <IconButton
            size='small'
            aria-label='previous quarter'
            onClick={() => stepTo(previousQuarterEnd(stepFrom))}
          >
            <ChevronLeftIcon color='secondary' />
          </IconButton>
        </Tooltip>
        <div title='As of date'>
          <CalendarCurrency
            value={shown}
            onChange={onChange}
            onAccept={onAccept}
            onOpen={onOpen}
            onClose={onClose}
            shortcutItems={shortcutItems}
            shortcutChangeImportance='set'
            closeOnSelect={false}
            sx={{ maxWidth: '12rem' }}
            debounceTime={TYPED_DATE_APPLY_MS}
            selectedCurrency={selectedCurrency}
          />
        </div>
        <Tooltip title='Next quarter'>
          <IconButton size='small' aria-label='next quarter' onClick={() => stepTo(nextQuarterEnd(stepFrom))}>
            <ChevronRightIcon color='secondary' />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
